'use client'

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

export interface DailyStats {
  date: string
  day_name: string
  call_count: number
  avg_score: number
  completed_count: number
}

// Custom tooltip component
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload || !payload.length) return null

  return (
    <div className='rounded-xl border border-white/60 bg-white/95 p-3 shadow-float backdrop-blur-sm'>
      <p className='mb-2 text-xs font-medium text-brand-navy'>{label}</p>
      <div className='space-y-1'>
        {payload.map((entry, index) => (
          <div key={index} className='flex items-center gap-2 text-xs'>
            <div
              className='h-2 w-2 rounded-full'
              style={{ backgroundColor: entry.color }}
            />
            <span className='capitalize text-muted-foreground'>
              {entry.name}:
            </span>
            <span className='font-semibold text-brand-navy'>
              {entry.name === 'Avg Score' 
                ? `${entry.value}%` 
                : entry.value.toLocaleString()
              }
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ActivityChartProps {
  className?: string
  title?: string
  description?: string
  data?: DailyStats[]
  totalCalls?: number
  averageScore?: number
  callsTrend?: number
  isLoading?: boolean
}

export function ActivityChart({
  className,
  title = 'Call Activity',
  description = 'Call analyses over the past 7 days',
  data = [],
  totalCalls = 0,
  averageScore = 0,
  callsTrend = 0,
  isLoading = false,
}: ActivityChartProps) {
  // Transform data for the chart
  const chartData = data.map((d) => ({
    name: d.day_name,
    calls: d.call_count,
    avgScore: d.avg_score,
    completed: d.completed_count,
  }))

  const isPositive = callsTrend >= 0

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden', className)}>
        <CardWatermark opacity={4} scale={1.2} />
        <CardHeader className='pb-2'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.activity className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            {title}
          </CardTitle>
          <p className='mt-1 text-sm text-muted-foreground'>{description}</p>
        </CardHeader>
        <CardContent className='pt-0'>
          <div className='mt-4 h-[240px] w-full flex items-center justify-center'>
            <Icons.loader className='h-8 w-8 animate-spin text-brand-cornflower' />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <CardWatermark opacity={4} scale={1.2} />

      <CardHeader className='pb-2'>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='flex items-center gap-2'>
              <Icons.activity
                className='h-5 w-5 text-brand-cornflower'
                strokeWidth={1.5}
              />
              {title}
            </CardTitle>
            <p className='mt-1 text-sm text-muted-foreground'>{description}</p>
          </div>

          {/* Quick Stats */}
          <div className='hidden items-center gap-4 sm:flex'>
            <div className='text-right'>
              <p className='text-micro uppercase text-brand-muted'>
                Total Calls
              </p>
              <p className='font-display text-lg font-bold text-brand-navy'>
                {totalCalls.toLocaleString()}
              </p>
            </div>
            <div className='h-10 w-px bg-border/50' />
            <div className='text-right'>
              <p className='text-micro uppercase text-brand-muted'>
                Avg Score
              </p>
              <p className='font-display text-lg font-bold text-brand-navy'>
                {averageScore}%
              </p>
            </div>
            {callsTrend !== 0 && (
              <motion.div
                className={cn(
                  'flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium',
                  isPositive
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'bg-red-50 text-red-500'
                )}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
              >
                {isPositive ? (
                  <Icons.trendingUp className='h-3 w-3' strokeWidth={2} />
                ) : (
                  <Icons.trendingUp
                    className='h-3 w-3 rotate-180'
                    strokeWidth={2}
                  />
                )}
                {isPositive ? '+' : ''}
                {callsTrend}%
              </motion.div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className='pt-0'>
        <div className='mt-4 h-[240px] w-full'>
          {chartData.length > 0 ? (
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  {/* Gradient for Calls */}
                  <linearGradient
                    id='gradientCalls'
                    x1='0'
                    y1='0'
                    x2='0'
                    y2='1'
                  >
                    <stop offset='0%' stopColor='#5B8DEF' stopOpacity={0.4} />
                    <stop offset='95%' stopColor='#5B8DEF' stopOpacity={0} />
                  </linearGradient>
                  {/* Gradient for Avg Score */}
                  <linearGradient
                    id='gradientScore'
                    x1='0'
                    y1='0'
                    x2='0'
                    y2='1'
                  >
                    <stop offset='0%' stopColor='#7C5CE7' stopOpacity={0.3} />
                    <stop offset='95%' stopColor='#7C5CE7' stopOpacity={0} />
                  </linearGradient>
                  {/* Gradient for Completed */}
                  <linearGradient id='gradientCompleted' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor='#10b981' stopOpacity={0.2} />
                    <stop offset='95%' stopColor='#10b981' stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray='3 3'
                  stroke='rgba(20, 26, 66, 0.06)'
                  vertical={false}
                />

                <XAxis
                  dataKey='name'
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: '#7B8AB8',
                    fontSize: 11,
                    fontWeight: 500,
                  }}
                  dy={8}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: '#7B8AB8',
                    fontSize: 11,
                  }}
                  tickFormatter={(value) =>
                    value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value
                  }
                />

                <Tooltip content={<CustomTooltip />} />

                {/* Completed - Background layer */}
                <Area
                  type='monotone'
                  dataKey='completed'
                  name='Completed'
                  stroke='#10b981'
                  strokeWidth={1.5}
                  fill='url(#gradientCompleted)'
                  dot={false}
                  activeDot={{
                    r: 4,
                    fill: '#10b981',
                    stroke: '#fff',
                    strokeWidth: 2,
                  }}
                />

                {/* Avg Score - Middle layer (scaled for visibility) */}
                <Area
                  type='monotone'
                  dataKey='avgScore'
                  name='Avg Score'
                  stroke='#7C5CE7'
                  strokeWidth={2}
                  fill='url(#gradientScore)'
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: '#7C5CE7',
                    stroke: '#fff',
                    strokeWidth: 2,
                  }}
                />

                {/* Calls - Top layer */}
                <Area
                  type='monotone'
                  dataKey='calls'
                  name='Calls'
                  stroke='#5B8DEF'
                  strokeWidth={2.5}
                  fill='url(#gradientCalls)'
                  dot={false}
                  activeDot={{
                    r: 6,
                    fill: '#5B8DEF',
                    stroke: '#fff',
                    strokeWidth: 2,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className='flex h-full flex-col items-center justify-center text-center'>
              <Icons.barChart className='h-12 w-12 text-gray-200 mb-3' />
              <p className='text-sm text-muted-foreground'>No call data yet</p>
              <p className='text-xs text-muted-foreground mt-1'>
                Upload your first call to see trends
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className='mt-4 flex items-center justify-center gap-6 border-t border-border/30 pt-4'>
          <div className='flex items-center gap-2'>
            <div className='h-2 w-2 rounded-full bg-brand-cornflower' />
            <span className='text-xs text-muted-foreground'>Calls</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='h-2 w-2 rounded-full bg-brand-purple' />
            <span className='text-xs text-muted-foreground'>Avg Score</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='h-2 w-2 rounded-full bg-emerald-500' />
            <span className='text-xs text-muted-foreground'>Completed</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
